import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

export interface Tag {
  id: string;
  name: string;
  description?: string;
  text_color: string;
  bg_color: string;
  created_at?: string;
  updated_at?: string;
}

export interface CreateTagRequest {
  name: string;
  description?: string;
  text_color: string;
  bg_color: string;
}

export interface UpdateTagRequest {
  name?: string;
  description?: string;
  text_color?: string;
  bg_color?: string;
}

export interface TagResponse {
  tags: Tag[];
}

export interface TagActionResponse {
  message: string;
  tag?: Tag;
}

export interface LinkResponse {
  message: string;
  success: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class TagsService {
  private apiUrl = '/api';
  private tagsSubject = new BehaviorSubject<Tag[]>([]);
  public tags$ = this.tagsSubject.asObservable();

  constructor(
    private http: HttpClient,
    private translate: TranslateService
  ) {}

  getTags(): Observable<TagResponse> {
    return this.http.get<TagResponse>(`${this.apiUrl}/tags/`).pipe(
      tap(response => {
        this.tagsSubject.next(response.tags || []);
      })
    );
  }

  getTagById(tagId: string): Observable<Tag> {
    return this.http.get<Tag>(`${this.apiUrl}/tags/${tagId}`);
  }

  createTag(tagData: CreateTagRequest): Observable<TagActionResponse> {
    return this.http.post<TagActionResponse>(`${this.apiUrl}/tags/`, tagData).pipe(
      tap(() => {
        this.refreshTags();
      })
    );
  }

  updateTag(tagId: string, tagData: UpdateTagRequest): Observable<TagActionResponse> {
    return this.http.put<TagActionResponse>(`${this.apiUrl}/tags/${tagId}`, tagData).pipe(
      tap(() => {
        this.refreshTags();
      })
    );
  }

  deleteTag(tagId: string): Observable<TagActionResponse> {
    return this.http.delete<TagActionResponse>(`${this.apiUrl}/tags/${tagId}`).pipe(
      tap(() => {
        const currenttags = this.tagsSubject.value;
        const updatedtags = currenttags.filter(tag => tag.id !== tagId);
        this.tagsSubject.next(updatedtags);
      })
    );
  }

  linkTag(tagId: string, tableName: string, itemId: string): Observable<LinkResponse> {
    return this.http.post<LinkResponse>(`${this.apiUrl}/tags/${tagId}/link/${tableName}/${itemId}`, {});
  }

  unlinkTag(tagId: string, tableName: string, itemId: string): Observable<LinkResponse> {
    return this.http.post<LinkResponse>(`${this.apiUrl}/tags/${tagId}/unlink/${tableName}/${itemId}`, {});
  }

  getTagsForItem(tableName: string, itemId: string): Observable<Tag[]> {
    return this.http.get<Tag[]>(`${this.apiUrl}/tags/item/${tableName}/${itemId}`);
  }

  refreshTags(): void {
    this.getTags().subscribe();
  }

  getTagsFromCache(): Tag[] {
    return this.tagsSubject.value;
  }

  isValidColor(color: string): boolean {
    const hexPattern = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    return hexPattern.test(color);
  }

  getContrastTextColor(backgroundColor: string): string {
    const hex = backgroundColor.replace('#', '');
    
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  }

  validateTagData(tagData: CreateTagRequest, excludeId?: string): string[] {
    const errors: string[] = [];

    if (!tagData.name || !tagData.name.trim()) {
      errors.push(this.translate.instant('TAGS.ERRORS.NAME_REQUIRED'));
      return errors;
    }

    if (tagData.name.trim().length > 50) {
      errors.push(this.translate.instant('TAGS.ERRORS.NAME_MAX_LENGTH'));
    }

    const trimmedName = tagData.name.trim().toLowerCase();
    const currentTags = this.getTagsFromCache();
    const existingTag = currentTags.find((tag: Tag) => 
      tag.name.toLowerCase() === trimmedName && 
      (!excludeId || tag.id !== excludeId)
    );

    if (existingTag) {
      errors.push(this.translate.instant('TAGS.ERRORS.DUPLICATE'));
    }

    if (!tagData.bg_color || !this.isValidHexColor(tagData.bg_color)) {
      errors.push(this.translate.instant('TAGS.ERRORS.INVALID_BG_COLOR'));
    }

    if (!tagData.text_color || !this.isValidHexColor(tagData.text_color)) {
      errors.push(this.translate.instant('TAGS.ERRORS.INVALID_TEXT_COLOR'));
    }

    return errors;
  }

  private isValidHexColor(color: string): boolean {
    const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    return hexColorRegex.test(color);
  }

  tagNameExists(name: string, excludeId?: string): boolean {
    const trimmedName = name.trim().toLowerCase();
    const currentTags = this.getTagsFromCache();
    return currentTags.some((tag: Tag) => 
      tag.name.toLowerCase() === trimmedName && 
      (!excludeId || tag.id !== excludeId)
    );
  }

  getPredefinedColors(): { name: string; bg_color: string; text_color: string }[] {
    return [
      { name: 'Verde Claro', bg_color: '#F0FFF0', text_color: '#000000' },
      { name: 'Azul Claro', bg_color: '#F0F8FF', text_color: '#000000' },
      { name: 'Amarelo Claro', bg_color: '#FFFACD', text_color: '#000000' },
      { name: 'Laranja Claro', bg_color: '#FFF0E6', text_color: '#000000' },
      { name: 'Rosa Claro', bg_color: '#FFF0F5', text_color: '#000000' },
      { name: 'Roxo Claro', bg_color: '#E6E6FA', text_color: '#000000' },
      { name: 'Lilás Claro', bg_color: '#F8EAF8', text_color: '#000000' },
      { name: 'Pink Claro', bg_color: '#FFE6F3', text_color: '#000000' },

      { name: 'Verde', bg_color: '#4CAF50', text_color: '#FFFFFF' },
      { name: 'Azul', bg_color: '#03A9F4', text_color: '#FFFFFF' },
      { name: 'Amarelo', bg_color: '#FFC107', text_color: '#000000' },
      { name: 'Laranja', bg_color: '#FF9800', text_color: '#FFFFFF' },
      { name: 'Coral', bg_color: '#FF7F50', text_color: '#FFFFFF' },
      { name: 'Roxo', bg_color: '#9C27B0', text_color: '#FFFFFF' },
      { name: 'Rosa Padrão', bg_color: '#FF009A', text_color: '#FFFFFF' },
      { name: 'Pink', bg_color: '#FF4081', text_color: '#FFFFFF' },

      { name: 'Verde Escuro', bg_color: '#388E3C', text_color: '#FFFFFF' },
      { name: 'Azul Escuro', bg_color: '#00ACC1', text_color: '#FFFFFF' },
      { name: 'Verde Oliva', bg_color: '#6B8E23', text_color: '#FFFFFF' },
      { name: 'Vermelho Escuro', bg_color: '#E64A19', text_color: '#FFFFFF' },
      { name: 'Vermelho', bg_color: '#D32F2F', text_color: '#FFFFFF' },
      { name: 'Roxo Escuro', bg_color: '#512DA8', text_color: '#FFFFFF' },
      { name: 'Violeta', bg_color: '#6A1B9A', text_color: '#FFFFFF' },
      { name: 'Azul Ardósia', bg_color: '#483D8B', text_color: '#FFFFFF' },

      { name: 'Cinza Claro', bg_color: '#F5F5F5', text_color: '#000000' },
      { name: 'Cinza Médio', bg_color: '#BDBDBD', text_color: '#000000' },
      { name: 'Cinza', bg_color: '#757575', text_color: '#FFFFFF' },
      { name: 'Cinza Azulado', bg_color: '#546E7A', text_color: '#FFFFFF' },
      { name: 'Cinza Escuro', bg_color: '#37474F', text_color: '#FFFFFF' },
      { name: 'Quase Preto', bg_color: '#212121', text_color: '#FFFFFF' },
      { name: 'Preto', bg_color: '#000000', text_color: '#FFFFFF' }
    ];
  }

  searchTags(query: string): Tag[] {
    const tags = this.getTagsFromCache();
    if (!query.trim()) {
      return tags;
    }
    
    return tags.filter(tag => 
      tag.name.toLowerCase().includes(query.toLowerCase())
    );
  }
}